import React from 'react';
import {
    Box,
    Typography,
    Button,
    Chip,
    Grid,
    Card,
    CardContent,
    CardHeader,
    CardActions,
    Divider,
    Tooltip,
    Checkbox,
} from '@mui/material';
import {
    Check,
    Close,
    CloudSync as CloudSyncIcon,
    Edit as EditIcon,
    Visibility,
    Work,
    People,
    Engineering,
    LocationCity,
    Numbers,
    CalendarMonth,
    Today,
    Info,
    Star,
    Apartment,
    Verified,
} from '@mui/icons-material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import {
    format, startOfWeek,
    addDays
} from 'date-fns';
import { fr } from 'date-fns/locale';
import * as DateTime from "luxon";
import { TimesheetCardProps } from '../interfaces/timesheet';
import { useUserRole } from '../context/userRoleContext';

const TimesheetCard: React.FC<TimesheetCardProps> = ({
    timesheet,
    onEdit,
    onValidateForeman,
    onValidateHR,
    userRole,
    onDelete,
    isOnline,
    isSelected = false,
    onSelect,
    showCheckbox = false,
    masterData
}) => {
    const { isAdmin } = useUserRole();

    // Total hours for the week
    const totalHours = timesheet.dayDetails
        ? Object.values(timesheet.dayDetails).reduce((total, dayDetail: any) => {
            const hours = dayDetail.hours || 0;
            const quarters = dayDetail.quarters || 0;
            return total + hours + quarters;
        }, 0)
        : 0;


    const canEdit = userRole && (
        (userRole === 'HR' && !timesheet.foreman_validated) ||
        (userRole === 'HR' && timesheet.foreman_validated) ||
        (userRole === 'Foreman' && !timesheet.foreman_validated && !timesheet.hr_validated) ||
        (userRole === 'Foreman' && timesheet.foreman_validated && !timesheet.hr_validated) ||
        (userRole === 'User' && !timesheet.hr_validated && !timesheet.foreman_validated)
    );

    const getWorkerMatricule = (): number | undefined => {
        // Find the worker in master data by name
        const worker = timesheet?.travailleur?.name ? timesheet.travailleur.name : '';
        const employee = masterData?.employees?.find(emp =>
            emp.name.toLocaleLowerCase().trim() === worker.toLocaleLowerCase().trim()
        );
        return employee ? employee.matricule : undefined;
    }

    return (
        <Card
            elevation={3}
            sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                borderRadius: "15px",
                boxShadow: "3px 2px 18px 0px rgba(0,0,0,0.1)",
                borderLeft: timesheet.foreman_validated && timesheet.hr_validated
                    ? '4px solid #4caf50'
                    : timesheet._pendingUpdate ? '4px solid #ff9800' : timesheet._pendingDelete
                        ? '4px solid #C62828' : !timesheet._pendingDelete && !timesheet._pendingUpdate && !timesheet.id
                            ? '4px solid #4caf50' : isSelected ? "4px solid #C62828" : '4px solid #e0e0e0',
                outline: isSelected ? 'none' : 'none',
                outlineColor: 'primary.main',
                transition: 'all 0.2s ease',
                transform: isSelected ? 'translateY(-3px)' : 'none'
            }}
        >
            <CardHeader
                avatar={
                    showCheckbox && timesheet.id && (!timesheet.foreman_validated || !timesheet.hr_validated) ? (
                        <Checkbox
                            disabled={!isOnline}
                            checked={isSelected}
                            onChange={() => onSelect && onSelect(timesheet.id as string)}
                            sx={{
                                '& .MuiSvgIcon-root': { fontSize: '1.3rem' },
                                color: 'primary.main',
                                transition: 'all 0.2s ease',
                                transform: isSelected ? 'scale(1.1)' : 'scale(1)',
                                '&:hover': {
                                    transform: 'scale(1.1)',
                                    color: 'primary.dark'
                                },
                                padding: '4px'
                            }}
                        />
                    ) : null
                }
                title={
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography variant="h6" component="div" sx={{ fontSize: '1rem', alignItems: 'center', display: 'flex', flexWrap: 'wrap' }}>
                            {/* display info message tooltip that displays teh remark if there's any remark */}
                            {timesheet.remark && (
                                <Tooltip title={timesheet.remark} placement="top"
                                    enterTouchDelay={0}
                                >
                                    <Info fontSize="large" color="warning" sx={{ mr: 1 }} />
                                </Tooltip>
                            )}

                            {/* Get the matricule of the emmployee from the master data */}
                            {(getWorkerMatricule()) && (
                                <Tooltip title={`${(getWorkerMatricule())}`}
                                    enterTouchDelay={0}
                                >
                                    <Chip
                                        size="small"
                                        label={`${((getWorkerMatricule())?.toString().length || 0) > 10 ?
                                            (getWorkerMatricule())?.toString().substring(0, 5) + '...' :
                                            (getWorkerMatricule())}`}
                                        color={getWorkerMatricule() ? "primary" : "error"}
                                        variant="filled"
                                        sx={{
                                            mr: 1,
                                            fontSize: '1rem',
                                            maxWidth: '110px',
                                            fontWeight: 'bold',
                                            '& .MuiChip-label': {
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap'
                                            }
                                        }}
                                    />
                                </Tooltip>
                            )}
                            {/* Replace this section with specific badges */}
                            {!timesheet._pendingDelete && !timesheet._pendingUpdate && !timesheet.id && (
                                <Chip
                                    size="small"
                                    label="Nouveau"
                                    color="success"
                                    icon={<CloudSyncIcon fontSize="small" />}
                                    sx={{ height: 20, fontSize: '0.7rem', mr: 1 }}
                                />
                            )}
                            {timesheet._pendingUpdate && (
                                <Chip
                                    size="small"
                                    label="Mis a jour"
                                    color="warning"
                                    icon={<CloudSyncIcon fontSize="small" />}
                                    sx={{ height: 20, fontSize: '0.7rem', mr: 1 }}
                                />
                            )}
                            {timesheet._pendingDelete && (
                                <Chip
                                    size="small"
                                    label="A supprimer"
                                    color="error"
                                    icon={<CloudSyncIcon fontSize="small" />}
                                    sx={{ height: 20, fontSize: '0.7rem', mr: 1 }}
                                />
                            )}
                            {timesheet.travailleur.name}
                            <div style={{ display: "flex", flexDirection: "column", justifyContent: "space-between", alignSelf: "end" }}>
                                {timesheet.travailleur.isInterim && (
                                    <Chip
                                        size="small"
                                        label={'Intérimaire'}
                                        sx={{ fontSize: '0.75rem', height: '20px', ml: 1, px: .5, mb: 1 }}
                                        color="secondary"
                                        variant="outlined"
                                        icon={<People fontSize="small" />}
                                    />
                                )}
                                {timesheet?.travailleur.interimCompanyName && <Chip
                                    size="small"
                                    label={timesheet?.travailleur.interimCompanyName}
                                    sx={{ fontSize: '0.75rem', height: '20px', ml: 1 }}
                                    color="default"
                                    variant="outlined"
                                    icon={<Apartment fontSize="small" />}
                                />}
                            </div>

                        </Typography>
                    </Box>
                }
            />
            <Divider />
            <CardContent sx={{ flexGrow: 1 }}>
                <Grid container spacing={1}>

                    <Grid item xs={12} md={12}>
                        <Typography variant="body2" color="textSecondary" gutterBottom sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                            <Verified color="primary" fontSize="small" sx={{ mr: 0.5 }} /> Validations:
                        </Typography>

                        <Grid item xs={12} sx={{ mt: 2, my: 1 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: "wrap" }}>
                                {(userRole?.toLocaleLowerCase() === 'Foreman'.toLocaleLowerCase() || userRole?.toLocaleLowerCase() === 'User'.toLocaleLowerCase() || isAdmin) && <Chip
                                    size="small"
                                    icon={timesheet.foreman_validated ? <Check /> : <Close />}
                                    label={timesheet.foreman_validated ? "Conducteur de travaux: Validé" : "Conducteur de travaux: en attente de validation"}
                                    color={timesheet.foreman_validated ? "success" : "warning"}
                                    sx={{ mr: 1, mb: 1 }}
                                    variant="outlined"
                                />}

                                {timesheet.id && !timesheet._pendingDelete && !timesheet._pendingUpdate && !timesheet.foreman_validated && (userRole?.toLocaleLowerCase() === "Foreman".toLocaleLowerCase() || userRole?.toLocaleLowerCase() === "HR".toLocaleLowerCase()) && (
                                    <Button
                                        size="small"
                                        variant="contained"
                                        color="success"
                                        onClick={() => onValidateForeman && onValidateForeman(timesheet.id as string)}
                                        disabled={!isOnline}
                                        sx={{ mr: 1, mb: 1, width: "100%" }}
                                    >
                                        Validation du conducteur de travaux
                                    </Button>
                                )}
                            </Box>
                        </Grid>
                        <Grid item xs={12} sx={{ mt: 2, my: 1 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: "wrap" }}>

                                {(userRole?.toLocaleLowerCase() === 'Foreman'.toLocaleLowerCase() || userRole?.toLocaleLowerCase() === 'User'.toLocaleLowerCase() || isAdmin) && <Chip
                                    size="small"
                                    variant='outlined'
                                    icon={timesheet.hr_validated ? <Check /> : <Close />}
                                    label={timesheet.hr_validated ? "RH: Validé" : "HR: en attente de validation"}
                                    color={timesheet.hr_validated ? "success" : "warning"}
                                    sx={{ mr: 1, mb: 1 }}
                                />}


                                {timesheet.id && !timesheet._pendingDelete && !timesheet._pendingUpdate && !timesheet.hr_validated && userRole?.toLocaleLowerCase() === "HR".toLocaleLowerCase() && (
                                    <Button
                                        size="small"
                                        variant="contained"
                                        color="success"
                                        onClick={() => onValidateHR && onValidateHR(timesheet.id as string)}
                                        disabled={!isOnline}
                                        sx={{ mr: 1, mb: 0, width: "100%" }}

                                    >
                                        Validation RH
                                    </Button>
                                )}
                            </Box>
                        </Grid>


                    </Grid>
                    <Divider sx={{ my: 1 }} />

                    {timesheet.created_by && (
                        <Grid item xs={12} sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                            <Typography variant="body2" color="textSecondary" sx={{ display: 'flex', alignItems: 'center' }}>
                                <People fontSize="small" sx={{ mr: 0.5 }} color="primary" /> Crée par:
                            </Typography>
                            <Typography variant="body2" component="div">
                                {timesheet.created_by}
                            </Typography>
                        </Grid>
                    )}
                    <Grid item xs={12} sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <Typography variant="body2" color="textSecondary" sx={{ display: 'flex', alignItems: 'center' }}>
                            <Engineering fontSize="small" sx={{ mr: 0.5 }} color="primary" /> Chef d'équipe:
                        </Typography>
                        <Typography variant="body2">{timesheet.chefChantier?.name}</Typography>
                    </Grid>
                    <Grid item xs={12} sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <Typography variant="body2" color="textSecondary" sx={{ display: 'flex', alignItems: 'center' }}>
                            <People fontSize="small" sx={{ mr: 0.5 }} color="primary" /> Conducteur de travaux:
                        </Typography>
                        <Typography variant="body2">{timesheet.chefProjet?.name}</Typography>
                    </Grid>
                    <Grid item xs={12} sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <Typography variant="body2" color="textSecondary" sx={{ display: 'flex', alignItems: 'center' }}>
                            <LocationCity fontSize="small" sx={{ mr: 0.5 }} color="primary" /> Chantier:
                        </Typography>
                        <Typography variant="body2">{timesheet.worksite?.name}</Typography>
                    </Grid>
                    <Grid item xs={12} sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <Typography variant="body2" color="textSecondary" sx={{ display: 'flex', alignItems: 'center' }}>
                            <Work fontSize="small" sx={{ mr: 0.5 }} color="primary" /> Total des heures:
                        </Typography>
                        <Typography variant="body2">{totalHours} heures</Typography>
                    </Grid>
                </Grid>
            </CardContent>
            <Divider />
            <CardContent sx={{ flexGrow: 1 }}>
                <Grid container spacing={1}>
                    <Grid item xs={12} sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <Typography variant="body2" color="textSecondary" sx={{ display: "flex", alignItems: "center" }}>
                            <Numbers color="primary" fontSize="small" sx={{ mr: 0.5 }} />Semaine:</Typography>
                        <Typography variant="body2">{timesheet.weekNumber}</Typography>
                    </Grid>
                    <Grid item xs={12} sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <Typography variant="body2" color="textSecondary" sx={{ display: "flex", alignItems: "center" }}>
                            <Today color="primary" fontSize="small" sx={{ mr: 0.5 }} />Date:</Typography>
                        <Typography variant="body2">{DateTime.DateTime.fromISO(timesheet.timesheetDate, { locale: "FR-fr" }
                        ).toFormat("dd/MM/yyyy")}</Typography>
                    </Grid>
                </Grid>
            </CardContent>
            <Divider sx={{ my: 1 }} />
            <CardContent sx={{ flexGrow: 1 }}>
                <Grid container spacing={1}>
                    <Grid item xs={12} md={12}>
                        <Typography variant="body2" color="textSecondary" gutterBottom sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                            <CalendarMonth color="primary" fontSize="small" sx={{ mr: 0.5 }} /> Semaine:
                        </Typography>
                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            mt: 0.5,
                        }}>
                            {['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi', 'dimanche'].map((day, index) => {
                                // Calculate the actual date for this day based on the timesheet date
                                const baseDate = timesheet.timesheetDate ? new Date(timesheet.timesheetDate) : new Date();
                                const startOfWeekDate = startOfWeek(baseDate, { locale: fr });
                                const dayDate = addDays(startOfWeekDate, index);
                                const formattedDate = format(dayDate, 'dd MMM', { locale: fr });

                                // Determine color based on the type of entry
                                let borderColor = 'grey.300';

                                // Check if this day has bonuses
                                const dayKey = day as keyof typeof timesheet.dayDetails;
                                const hasBonuses = timesheet.dayDetails?.[dayKey]?.bonuses &&
                                    timesheet.dayDetails[dayKey]?.bonuses?.length > 0;

                                // Generate tooltip content
                                let tooltipContent = (
                                    <Box>
                                        <Typography fontWeight="bold" fontFamily={'"Noto Sans", "Roboto", "Helvetica", "Arial", sans-serif'} variant="body1" sx={{ mb: 1 }}>
                                            {day.charAt(0).toUpperCase() + day.slice(1)} ({formattedDate})
                                            {/* Display worked hours for this day */}
                                            <br></br>
                                            <Typography variant="caption" sx={{ mt: 0.5 }}>
                                                Heures travaillées: {(timesheet.dayDetails?.[dayKey]?.hours + timesheet.dayDetails?.[dayKey]?.quarters) || 0}h
                                            </Typography>
                                        </Typography>

                                        {hasBonuses && (
                                            <Box>
                                                <Typography variant="body2" fontWeight="bold" sx={{ mt: 1 }}>
                                                    Autres Codes salaires ({timesheet.dayDetails[dayKey]?.bonuses?.length}):
                                                </Typography>
                                                {timesheet.dayDetails[dayKey]?.bonuses?.map((bonus, i) => (
                                                    <Typography variant="body2" key={i} sx={{ ml: 1 }}>
                                                        • {bonus.code}: {bonus.hours}h
                                                    </Typography>
                                                ))}
                                            </Box>
                                        )}
                                    </Box>
                                );

                                return (
                                    <Tooltip
                                        key={day}
                                        title={tooltipContent}
                                        placement="top"
                                        arrow
                                        enterTouchDelay={0}
                                        componentsProps={{
                                            tooltip: {
                                                sx: {
                                                    bgcolor: 'primary.dark',
                                                    '& .MuiTooltip-arrow': {
                                                        color: 'primary.dark',
                                                    },
                                                    borderRadius: '8px',
                                                    px: 2,
                                                    py: 1,
                                                    boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.15)',
                                                    fontSize: '0.75rem',
                                                    fontWeight: 500,
                                                    maxWidth: '250px'
                                                }
                                            }
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                width: '32px',
                                                height: '32px',
                                                display: 'flex',
                                                justifyContent: 'center',
                                                alignItems: 'center',
                                                borderRadius: '8px',
                                                border: '1px solid',
                                                borderColor: hasBonuses ? 'warning.main' : borderColor,
                                                fontSize: '0.85rem',
                                                fontWeight: 600,
                                                color: 'text.primary',
                                                backgroundColor: hasBonuses ? 'warning.light' : 'background.paper',
                                                position: 'relative',
                                                transition: 'all 0.2s ease-in-out',
                                                boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.05)',
                                                '&:hover': {
                                                    transform: 'translateY(-2px)',
                                                    boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)',
                                                    borderColor: 'primary.main',
                                                    color: 'primary.main',
                                                },
                                                letterSpacing: '0.5px',
                                                fontFamily: '"Noto Sans", "Roboto", "Helvetica", "Arial", sans-serif',
                                            }}
                                        >
                                            {day.charAt(0).toUpperCase()}
                                            {hasBonuses && (
                                                <Star
                                                    sx={{
                                                        position: 'absolute',
                                                        top: '-8px',
                                                        right: '-8px',
                                                        fontSize: '14px',
                                                        color: 'warning.main',
                                                        backgroundColor: '#fff',
                                                        borderRadius: '50%',
                                                        padding: '1px'
                                                    }}
                                                />
                                            )}
                                        </Box>
                                    </Tooltip>
                                );
                            })}
                        </Box>
                    </Grid>
                </Grid>
            </CardContent>
            <Divider />

            <CardActions sx={{ justifyContent: 'space-between', padding: 0, height: "auto" }}>
                <Box sx={{ display: 'flex', width: "100%", height: "100%", alignItems: 'stretch' }}>
                    {!timesheet._pendingDelete && canEdit && (
                        <Button
                            size="small"
                            onClick={() => onEdit(timesheet)}
                            startIcon={<EditIcon />}
                            variant='contained'
                            color="primary"
                            sx={{
                                flexGrow: 1,
                                borderRadius: 0,
                                py: 2,
                                boxShadow: "none",
                                height: "100%",
                                display: 'flex',
                                m: 0
                            }}
                        >
                            Modifier
                        </Button>
                    )}
                    {!timesheet._pendingDelete && !canEdit && (
                        <Button
                            size="small"
                            onClick={() => onEdit(timesheet)}
                            startIcon={<Visibility />}
                            variant='contained'
                            color="success"
                            sx={{
                                flexGrow: 1,
                                borderRadius: 0,
                                py: 2,
                                boxShadow: "none",
                                height: "100%",
                                display: 'flex',
                                m: 0
                            }}
                        >
                            Voir
                        </Button>
                    )}

                    {(!timesheet._pendingDelete && canEdit) &&
                        <Button
                            size="small"
                            onClick={() => onDelete(timesheet)}
                            variant="outlined"
                            color="error"
                            sx={{
                                minWidth: '50px',
                                borderRadius: 0,
                                py: 2,
                                boxShadow: "none",
                                height: "100%",
                                borderBottomRightRadius: 15,
                                m: 0
                            }}
                        >
                            <DeleteIcon />
                        </Button>
                    }
                </Box>
            </CardActions>
        </Card>
    );
};
export default TimesheetCard;